/**
 * Friendly names for the exact model ids the OrbitalX stack uses today. No
 * prefix walk, no dated-pin tolerance, no raw-id fallback — adding a new
 * model id means adding an entry here. An unknown id renders as no footer
 * + a console.error so the bug (forgot to map a model the AI service started
 * sending) is visible, not silently soft-displayed.
 *
 * Production model set (per ORB-294):
 *   - claude-opus-4-8 → "Opus 4.8" (LP + email asset rebuild agent)
 *   - gpt-4o          → "GPT-4o"  (Webster completion-message generator)
 *   - gpt-4o-mini     → "GPT-4o mini" (lightweight Webster paths)
 */
const MODEL_LABELS = {
    'claude-opus-4-8': 'Opus 4.8',
    'gpt-4o': 'GPT-4o',
    'gpt-4o-mini': 'GPT-4o mini',
} as const

const tokenFormatter = new Intl.NumberFormat('en-US')

interface MessageTokenFooterProps {
    modelId?: string | null
    outputTokens?: number | null
}

/**
 * The model/token attribution caption rendered below a finalized Webster
 * message. Elapsed-time signals "Webster is still working" during the turn;
 * this footer is the after-the-fact attribution: which model produced THIS
 * specific message, and how many output tokens it cost.
 *
 * Each assistant message gets its own footer because a single user turn
 * produces two persisted assistant messages: the analyzer's reply (often
 * gpt-4o) and the closer ("Page updated." with the agent's Opus 4.8). Per-
 * message stamping keeps the attribution honest about the mix-match.
 *
 * Renders nothing — and logs a console.error — when modelId is unrecognized.
 * That's deliberate: silently rendering the raw id (or guessing via prefix
 * match) would hide a real "we forgot to add this model to MODEL_LABELS"
 * bug. Visible miss > soft fallback.
 */
export function MessageTokenFooter({ modelId, outputTokens }: MessageTokenFooterProps) {
    if (!modelId) return null
    if (!outputTokens || outputTokens <= 0) return null

    const label = MODEL_LABELS[modelId as keyof typeof MODEL_LABELS]
    if (!label) {
        // Surface unrecognized ids loudly so we notice and update the map.
        // Returning null here keeps the chat surface clean — the raw id would
        // be ugly and the wrong-friendly-name (e.g. "GPT-4o" for an
        // unmapped "gpt-4o-mini") would mislead.
        console.error(`MessageTokenFooter: unknown modelId "${modelId}" — add it to MODEL_LABELS`)
        return null
    }

    return (
        <span className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
            {' · '}{label} · {tokenFormatter.format(outputTokens)} tokens
        </span>
    )
}
